import React from 'react';
import OmCircularProgress from './OmCircularProgress';

const LoadingState = ({ 
  size = 'default', 
  text = 'Loading...', 
  showText = true,
  variant = 'spinner',
  className = '',
  fullScreen = false
}) => {
  const getSizeClasses = (size) => {
    switch (size) {
      case 'small':
        return {
          spinner: 'small',
          text: 'text-sm',
          container: 'space-y-2'
        };
      case 'large':
        return {
          spinner: 'large',
          text: 'text-lg',
          container: 'space-y-6'
        };
      case 'extra-large':
        return {
          spinner: 'large',
          text: 'text-xl',
          container: 'space-y-8'
        };
      default:
        return {
          spinner: 'medium',
          text: 'text-base',
          container: 'space-y-4'
        };
    }
  };

  const sizeClasses = getSizeClasses(size);

  const SpinnerComponent = () => (
    <div className="flex justify-center">
      <OmCircularProgress
        indeterminate={true}
        size={sizeClasses.spinner}
      />
    </div>
  );

  const DotsComponent = () => (
    <div className="flex space-x-2" role="status" aria-label="Loading">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={`
            ${size === 'small' ? 'h-2 w-2' : size === 'large' ? 'h-4 w-4' : 'h-3 w-3'}
            bg-fresh-leaf animate-pulse
          `}
          style={{
            animationDelay: `${i * 0.2}s`,
            animationDuration: '0.8s'
          }}
        />
      ))}
    </div>
  );

  const PulseComponent = () => (
    <div className={`${sizeClasses.container} animate-pulse`} role="status" aria-label="Loading">
      <div className="space-y-3">
        <div className="h-4 bg-silver-mist w-3/4"></div>
        <div className="h-4 bg-silver-mist w-1/2"></div>
        <div className="h-4 bg-silver-mist w-5/6"></div>
      </div>
    </div>
  );

  const BarComponent = () => (
    <div className="w-full bg-silver-mist h-2" role="status" aria-label="Loading">
      <div 
        className="bg-fresh-leaf h-2 animate-pulse"
        style={{
          width: '100%',
          animation: 'loading-bar 1.5s ease-in-out infinite'
        }}
      />
      <style jsx>{`
        @keyframes loading-bar {
          0% { width: 0%; }
          50% { width: 70%; }
          100% { width: 100%; }
        }
      `}</style>
    </div>
  );

  const renderLoadingIndicator = () => {
    switch (variant) {
      case 'dots':
        return <DotsComponent />;
      case 'pulse':
        return <PulseComponent />;
      case 'bar':
        return <BarComponent />;
      case 'spinner':
      default:
        return <SpinnerComponent />;
    }
  };

  const containerClasses = fullScreen
    ? 'min-h-screen flex items-center justify-center bg-silver-mist'
    : 'flex items-center justify-center py-8';

  return (
    <div className={`${containerClasses} ${className}`}>
      <div className={`text-center ${sizeClasses.container}`}>
        {variant !== 'pulse' && renderLoadingIndicator()}
        {variant === 'pulse' && renderLoadingIndicator()}
        
        {showText && variant !== 'pulse' && (
          <div className={`${sizeClasses.text} text-steel font-medium`}>
            {text}
          </div>
        )}
      </div>
    </div>
  );
};

// Additional preset loading states for common use cases
export const TableLoadingState = ({ rows = 5, columns = 4 }) => (
  <div className="animate-pulse space-y-4">
    {[...Array(rows)].map((_, rowIndex) => (
      <div key={rowIndex} className="flex space-x-4">
        {[...Array(columns)].map((_, colIndex) => (
          <div
            key={colIndex}
            className={`h-4 bg-silver-mist ${
              colIndex === 0 ? 'w-1/4' : 
              colIndex === 1 ? 'w-1/3' : 
              colIndex === 2 ? 'w-1/5' : 'w-1/6'
            }`}
          />
        ))}
      </div>
    ))}
  </div>
);

export const CardLoadingState = () => (
  <div className="animate-pulse">
    <div className="bg-crystal-white shadow-minimal border border-silver-mist p-6">
      <div className="space-y-4">
        <div className="h-4 bg-silver-mist w-3/4"></div>
        <div className="h-8 bg-silver-mist w-1/2"></div>
        <div className="space-y-2">
          <div className="h-3 bg-silver-mist"></div>
          <div className="h-3 bg-silver-mist w-5/6"></div>
        </div>
      </div>
    </div>
  </div>
);

export const ListLoadingState = ({ items = 3 }) => (
  <div className="animate-pulse space-y-3">
    {[...Array(items)].map((_, index) => (
      <div key={index} className="flex items-center space-x-3">
        <div className="h-10 w-10 bg-silver-mist"></div>
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-silver-mist w-3/4"></div>
          <div className="h-3 bg-silver-mist w-1/2"></div>
        </div>
      </div>
    ))}
  </div>
);

export default LoadingState;
